import { computed } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';

// SiteUp — Modo Foco: preset da workbench (nav vira rail 56px, lista compacta,
// painel de contato fecha e sobra o rail direito). Estado em ui_settings:
//   focus_mode_enabled        → modo ativo (undefined = nunca escolheu)
//   focus_mode_onboarded      → legado: já viu o card antigo (migra p/ guide)
//   focus_mode_guide          → { welcome_seen, tour_completed, tour_last_step, entries_count }
//   focus_mode_dock_position  → { xPct, yPct } do cluster (viewport, 0–1)
//   focus_mode_menu_side      → auto | up | down | left | right
export const FOCUS_MENU_SIDES = ['auto', 'up', 'down', 'left', 'right'];
export const FOCUS_MENU_OPPOSITE = {
  up: 'down',
  down: 'up',
  left: 'right',
  right: 'left',
};

const MENU_FIT_PX = 200;

export function resolveFocusMenuSide(preferred, rect, viewport) {
  const gap = 8;
  const spaces = {
    up: rect.top - gap,
    down: viewport.height - rect.bottom - gap,
    left: rect.left - gap,
    right: viewport.width - rect.right - gap,
  };
  const fits = side => spaces[side] >= MENU_FIT_PX;
  const ranked = ['up', 'down', 'right', 'left'].sort(
    (a, b) => spaces[b] - spaces[a]
  );

  if (preferred === 'auto' || !FOCUS_MENU_OPPOSITE[preferred]) {
    return ranked.find(fits) || ranked[0];
  }
  if (fits(preferred)) return preferred;
  return FOCUS_MENU_OPPOSITE[preferred];
}

export function useFocusMode() {
  const { uiSettings, updateUISettings } = useUISettings();

  // true | false | undefined (nunca configurou — deixa o default por viewport)
  const focusModeSetting = computed(() => {
    const value = uiSettings.value?.focus_mode_enabled;
    return typeof value === 'boolean' ? value : undefined;
  });

  const isFocusModeEnabled = computed(
    () => uiSettings.value?.focus_mode_enabled === true
  );

  const isFocusModeOnboarded = computed(
    () => uiSettings.value?.focus_mode_onboarded === true
  );

  const focusModeGuide = computed(() => {
    const raw = uiSettings.value?.focus_mode_guide;
    const onboarded = uiSettings.value?.focus_mode_onboarded === true;
    const fromStore = raw && typeof raw === 'object' ? raw : {};
    const lastStep = Number(fromStore.tour_last_step);
    const entries = Number(fromStore.entries_count);
    return {
      welcome_seen: fromStore.welcome_seen === true || onboarded,
      tour_completed: fromStore.tour_completed === true,
      tour_last_step: Number.isFinite(lastStep) ? lastStep : 0,
      entries_count: Number.isFinite(entries) ? entries : 0,
    };
  });

  const shouldShowWelcome = computed(() => !focusModeGuide.value.welcome_seen);

  const patchFocusGuide = partial => {
    updateUISettings({
      focus_mode_guide: { ...focusModeGuide.value, ...partial },
      ...(partial.welcome_seen ? { focus_mode_onboarded: true } : {}),
    });
  };

  const markWelcomeSeen = () => {
    if (focusModeGuide.value.welcome_seen) return;
    patchFocusGuide({ welcome_seen: true });
  };

  const markTourCompleted = () => {
    patchFocusGuide({
      welcome_seen: true,
      tour_completed: true,
    });
  };

  const saveTourStep = step => {
    patchFocusGuide({ tour_last_step: step });
  };

  const restartFocusGuide = () => {
    patchFocusGuide({
      welcome_seen: true,
      tour_completed: false,
      tour_last_step: 0,
    });
  };

  const enableFocusMode = () => {
    const currentNav = uiSettings.value?.sidebar_width;
    const guide = focusModeGuide.value;
    const firstActivation = !guide.welcome_seen && guide.entries_count === 0;
    const patch = {
      focus_mode_enabled: true,
      workbench_list_compact: true,
      workbench_list_mode: 'avatars',
      workbench_list_peek: true,
      is_contact_sidebar_open: false,
      sidebar_width: 56,
      focus_mode_guide: {
        ...guide,
        entries_count: guide.entries_count + 1,
      },
    };
    if (typeof currentNav === 'number' && currentNav > 56) {
      patch.workbench_nav_width_before_focus = currentNav;
    }
    updateUISettings(patch);
    return firstActivation;
  };

  const disableFocusMode = () => {
    const restore = uiSettings.value?.workbench_nav_width_before_focus;
    const patch = { focus_mode_enabled: false };
    if (typeof restore === 'number' && restore >= 56) {
      patch.sidebar_width = restore;
    }
    updateUISettings(patch);
  };

  const toggleFocusMode = () => {
    if (isFocusModeEnabled.value) {
      disableFocusMode();
      return false;
    }
    return enableFocusMode();
  };

  const markFocusModeOnboarded = () => {
    if (isFocusModeOnboarded.value) return;
    updateUISettings({ focus_mode_onboarded: true });
  };

  const dockPosition = computed(() => {
    const raw = uiSettings.value?.focus_mode_dock_position;
    if (
      raw &&
      typeof raw.xPct === 'number' &&
      typeof raw.yPct === 'number' &&
      Number.isFinite(raw.xPct) &&
      Number.isFinite(raw.yPct)
    ) {
      return { xPct: raw.xPct, yPct: raw.yPct };
    }
    return null;
  });

  const saveDockPosition = pos => {
    if (!pos || typeof pos.xPct !== 'number' || typeof pos.yPct !== 'number') {
      return;
    }
    updateUISettings({
      focus_mode_dock_position: { xPct: pos.xPct, yPct: pos.yPct },
    });
  };

  const menuSide = computed(() => {
    const raw = uiSettings.value?.focus_mode_menu_side;
    return FOCUS_MENU_SIDES.includes(raw) ? raw : 'auto';
  });

  const setMenuSide = side => {
    if (!FOCUS_MENU_SIDES.includes(side)) return;
    updateUISettings({ focus_mode_menu_side: side });
  };

  return {
    focusModeSetting,
    isFocusModeEnabled,
    isFocusModeOnboarded,
    enableFocusMode,
    disableFocusMode,
    toggleFocusMode,
    markFocusModeOnboarded,
    focusModeGuide,
    shouldShowWelcome,
    markWelcomeSeen,
    markTourCompleted,
    saveTourStep,
    restartFocusGuide,
    dockPosition,
    saveDockPosition,
    menuSide,
    setMenuSide,
  };
}
